import React, { createContext, useContext, useState, useCallback, useRef } from 'react';
import { useAuth } from '../../components/AuthProvider';
import { useTabPersistence } from './useTabPersistence';
import { useTabHistory } from './useTabHistory';
import { generateTabId, buildTabPath, createCampaignsHomeTab } from './utils';

/**
 * TabsContext
 * 
 * Manages browser-style tabs for navigating between campaigns, templates, and saved views.
 * Each tab remembers:
 * - What item is open (campaign, template, or saved view)
 * - What view mode is active (flow, kanban, table, etc.)
 * - The URL path for the tab (for deep linking)
 * - Navigation history (session-only, for back/forward within tab)
 * - Open mission + doc stack + doc scroll (persisted — restored on app reopen)
 * - Library doc trail + scroll per doc tab (docStack, docScrollById)
 *
 * Tabs are persisted to user settings on the backend (excluding history).
 */

const TabsContext = createContext(null);

export function TabsProvider({ children, onNavigate }) {
  const { workspaceId } = useAuth();
  const [tabs, setTabs] = useState([]);
  const [activeTabId, setActiveTabId] = useState(null);
  const [isLoaded, setIsLoaded] = useState(false);

  // TabBar calls switchToTab then onTabClick; skip duplicate handleTabClick from useTabIntegration's effect.
  const skipNextActiveTabEffectRef = useRef(false);
  const consumeSkipNextActiveTabEffect = useCallback(() => {
    if (!skipNextActiveTabEffectRef.current) return false;
    skipNextActiveTabEffectRef.current = false;
    return true;
  }, []);

  // Persistence (save/load to backend) — tab sets are per-workspace
  const { saveTabs } = useTabPersistence({ setTabs, setActiveTabId, setIsLoaded, workspaceId });

  // History navigation (back/forward within tab)
  const { canGoBack, canGoForward, goBack, goForward } = useTabHistory({
    tabs,
    activeTabId,
    setTabs,
    saveTabs,
  });

  // ═══════════════════════════════════════════════════════════════
  // TAB CRUD OPERATIONS
  // ═══════════════════════════════════════════════════════════════

  /**
   * Open a new tab or switch to existing tab for the same item
   * @param {boolean} allowDuplicate - If true, always create a new tab even if one exists
   * @returns {object} The tab that was opened or switched to
   */
  const openTab = useCallback(({ type, targetId = null, label, icon, viewMode = 'flow', customViewId = null, allowDuplicate = false, docStack, docScrollById }) => {
    // Blank tabs are always unique; never reuse an existing blank tab
    const isBlank = type === 'blank';

    // Check if tab for this item already exists (unless duplicates are allowed)
    if (!allowDuplicate && !isBlank) {
      const existingTab = tabs.find(t => t.type === type && t.targetId === targetId);
      
      if (existingTab) {
        // Switch to existing tab (and update viewMode/customViewId if different)
        const needsUpdate = existingTab.viewMode !== viewMode || existingTab.customViewId !== customViewId;
        const updatedTab = needsUpdate
          ? { ...existingTab, viewMode, customViewId, path: buildTabPath(type, targetId, viewMode, customViewId) }
          : existingTab;
        
        if (updatedTab !== existingTab) {
          const newTabs = tabs.map(t => t.id === existingTab.id ? updatedTab : t);
          setTabs(newTabs);
          saveTabs(newTabs, existingTab.id);
        } else {
          saveTabs(tabs, existingTab.id);
        }
        
        setActiveTabId(existingTab.id);
        return updatedTab;
      }
    }

    const resolvedLabel = isBlank
      ? (label || 'New Tab')
      : (label || (type === 'doc' ? 'Untitled Doc' : type === 'docs' ? 'Documents' : type === 'dashboards' ? 'Dashboards' : type === 'campaigns' ? 'Campaigns' : 'Untitled'));
    const resolvedIcon = isBlank || type === 'doc' || type === 'docs' || type === 'dashboards' || type === 'campaigns'
      ? (icon ?? null)
      : (icon || (type === 'template' ? '📋' : '📁'));
    
    // Create new tab with initial history entry
    const initialHistoryEntry = {
      type,
      targetId,
      viewMode: isBlank ? null : viewMode,
      customViewId: isBlank ? null : customViewId,
      label: resolvedLabel,
      icon: resolvedIcon,
    };
    
    const newTab = {
      id: generateTabId(),
      type,
      targetId,
      label: resolvedLabel,
      icon: resolvedIcon,
      viewMode: isBlank ? null : viewMode,
      customViewId: isBlank ? null : customViewId,
      path: buildTabPath(type, targetId, viewMode, customViewId),
      history: [initialHistoryEntry],
      historyIndex: 0,
      ...(type === 'doc' ? {
        docStack: Array.isArray(docStack) && docStack.length
          ? docStack
          : (targetId ? [targetId] : []),
        docScrollById: docScrollById && typeof docScrollById === 'object'
          ? docScrollById
          : {},
      } : {}),
    };
    
    const newTabs = [...tabs, newTab];
    setTabs(newTabs);
    setActiveTabId(newTab.id);
    saveTabs(newTabs, newTab.id);
    return newTab;
  }, [tabs, saveTabs]);

  /**
   * Put a place on the current tab. Sidebar modes (Docs / Dashboards /
   * Campaigns) always reuse this chip — they never spawn a second tab.
   * If another tab already is that same place, it is closed.
   */
  const showInActiveTab = useCallback((fields) => {
    const payload = {
      type: fields.type,
      targetId: fields.targetId ?? null,
      label: fields.label,
      icon: fields.icon ?? null,
      viewMode: fields.viewMode ?? null,
      customViewId: fields.customViewId ?? null,
      docStack: fields.docStack ?? [],
      docScrollById: fields.docScrollById ?? {},
      openMissionId: null,
      missionDocStack: [],
      missionDocScrollById: {},
      missionDocDirect: false,
    };

    if (!activeTabId) {
      return openTab(payload);
    }

    const current = tabs.find((t) => t.id === activeTabId);
    if (
      current &&
      current.type === payload.type &&
      (current.targetId ?? null) === payload.targetId
    ) {
      return current;
    }

    setTabs((prevTabs) => {
      const next = prevTabs
        .filter((t) => {
          if (t.id === activeTabId) return true;
          if (payload.type === 'blank') return true;
          return !(
            t.type === payload.type &&
            (t.targetId ?? null) === payload.targetId
          );
        })
        .map((t) => {
          if (t.id !== activeTabId) return t;
          const updated = { ...t, ...payload };
          updated.path = buildTabPath(
            updated.type,
            updated.targetId,
            updated.viewMode,
            updated.customViewId
          );
          return updated;
        });
      saveTabs(next, activeTabId);
      return next;
    });
  }, [activeTabId, tabs, openTab, saveTabs]);

  const openDocsHomeTab = useCallback(() => {
    return showInActiveTab({
      type: 'docs',
      targetId: null,
      label: 'Documents',
      icon: null,
      viewMode: null,
    });
  }, [showInActiveTab]);

  const openDashboardsHomeTab = useCallback(() => {
    return showInActiveTab({
      type: 'dashboards',
      targetId: null,
      label: 'Dashboards',
      icon: null,
      viewMode: null,
    });
  }, [showInActiveTab]);

  const openCampaignsHomeTab = useCallback(() => {
    return showInActiveTab({
      type: 'campaigns',
      targetId: null,
      label: 'Campaigns',
      icon: null,
      viewMode: null,
    });
  }, [showInActiveTab]);

  /**
   * Close a tab
   */
  const closeTab = useCallback((tabId) => {
    setTabs(prevTabs => {
      const tabIndex = prevTabs.findIndex(t => t.id === tabId);
      if (tabIndex === -1) return prevTabs;
      
      let newTabs = prevTabs.filter(t => t.id !== tabId);
      
      // If we're closing the active tab, switch to adjacent tab.
      // The last chip must never disappear — Campaigns home owns the empty place.
      let newActiveId = activeTabId;
      if (activeTabId === tabId) {
        if (newTabs.length === 0) {
          const home = createCampaignsHomeTab();
          newTabs = [home];
          newActiveId = home.id;
        } else if (tabIndex >= newTabs.length) {
          newActiveId = newTabs[newTabs.length - 1].id;
        } else {
          newActiveId = newTabs[tabIndex].id;
        }
        setActiveTabId(newActiveId);
      }
      
      saveTabs(newTabs, newActiveId);
      return newTabs;
    });
  }, [activeTabId, saveTabs]);

  /**
   * Switch to a tab
   */
  const switchToTab = useCallback((tabId) => {
    const tab = tabs.find(t => t.id === tabId);
    if (!tab) return;

    skipNextActiveTabEffectRef.current = true;
    setActiveTabId(tabId);
    saveTabs(tabs, tabId);
    
    // Trigger navigation callback
    if (onNavigate) {
      onNavigate(tab);
    }
  }, [tabs, saveTabs, onNavigate]);

  /**
   * Update tab metadata (label, icon, viewMode, customViewId, session overlay state)
   * @param {string} tabId - Tab to update
   * @param {Object} updates - Properties to update
   * @param {Object} options - Options
   * @param {boolean} options.addToHistory - Whether to add this as a new history entry
   * @param {boolean} options.persist - Whether to debounce-save to backend (default true).
   *   Pass false for session-only fields like history updates that shouldn't hit the API.
   */
  const updateTab = useCallback((tabId, updates, options = {}) => {
    const { addToHistory = false, persist = true } = options;
    
    setTabs(prevTabs => {
      const newTabs = prevTabs.map(t => {
        if (t.id !== tabId) return t;
        
        const updatedTab = { ...t, ...updates };
        
        // Rebuild path if type, targetId, viewMode, or customViewId changed
        if (updates.type || updates.targetId || updates.viewMode || updates.customViewId !== undefined) {
          updatedTab.path = buildTabPath(
            updatedTab.type,
            updatedTab.targetId,
            updatedTab.viewMode,
            updatedTab.customViewId
          );
        }
        
        // Add to history if requested
        if (addToHistory) {
          const newHistoryEntry = {
            type: updatedTab.type,
            targetId: updatedTab.targetId,
            viewMode: updatedTab.viewMode,
            customViewId: updatedTab.customViewId,
            label: updatedTab.label,
            icon: updatedTab.icon,
          };
          
          // Initialize history if not present
          const currentHistory = t.history || [];
          const currentIndex = t.historyIndex ?? (currentHistory.length - 1);
          
          // Check if this entry is the same as the current one (prevent duplicates)
          const currentEntry = currentHistory[currentIndex];
          const isDuplicate = currentEntry &&
            currentEntry.type === newHistoryEntry.type &&
            currentEntry.targetId === newHistoryEntry.targetId &&
            currentEntry.viewMode === newHistoryEntry.viewMode &&
            currentEntry.customViewId === newHistoryEntry.customViewId;
          
          if (!isDuplicate) {
            // Truncate forward history and add new entry
            const newHistory = [...currentHistory.slice(0, currentIndex + 1), newHistoryEntry];
            updatedTab.history = newHistory;
            updatedTab.historyIndex = newHistory.length - 1;
          }
        }
        
        return updatedTab;
      });
      if (persist) saveTabs(newTabs, activeTabId);
      return newTabs;
    });
  }, [activeTabId, saveTabs]);

  /**
   * Update the active tab's view mode
   */
  const setActiveTabViewMode = useCallback((viewMode) => {
    if (!activeTabId) return;
    updateTab(activeTabId, { viewMode });
  }, [activeTabId, updateTab]);

  // ═══════════════════════════════════════════════════════════════
  // BULK OPERATIONS
  // ═══════════════════════════════════════════════════════════════

  /**
   * Reorder tabs (for drag and drop)
   */
  const reorderTabs = useCallback((fromIndex, toIndex) => {
    setTabs(prevTabs => {
      const newTabs = [...prevTabs];
      const [removed] = newTabs.splice(fromIndex, 1);
      newTabs.splice(toIndex, 0, removed);
      saveTabs(newTabs, activeTabId);
      return newTabs;
    });
  }, [activeTabId, saveTabs]);

  /**
   * Close all tabs except the specified one
   */
  const closeOtherTabs = useCallback((tabId) => {
    setTabs(prevTabs => {
      const tab = prevTabs.find(t => t.id === tabId);
      if (!tab) return prevTabs;
      
      const newTabs = [tab];
      setActiveTabId(tabId);
      saveTabs(newTabs, tabId);
      return newTabs;
    });
  }, [saveTabs]);

  /**
   * Close all tabs
   */
  const closeAllTabs = useCallback(() => {
    const home = createCampaignsHomeTab();
    setTabs([home]);
    setActiveTabId(home.id);
    saveTabs([home], home.id);
  }, [saveTabs]);

  // ═══════════════════════════════════════════════════════════════
  // UTILITY FUNCTIONS
  // ═══════════════════════════════════════════════════════════════

  // Get the currently active tab
  const activeTab = tabs.find(t => t.id === activeTabId) || null;

  /**
   * Find a tab by type and targetId
   */
  const findTab = useCallback((type, targetId) => {
    return tabs.find(t => t.type === type && t.targetId === targetId) || null;
  }, [tabs]);

  /**
   * Get the URL path for the active tab
   */
  const getActiveTabPath = useCallback(() => {
    if (!activeTab) return '/';
    return activeTab.path || buildTabPath(activeTab.type, activeTab.targetId, activeTab.viewMode, activeTab.customViewId);
  }, [activeTab]);

  const value = {
    tabs,
    activeTabId,
    activeTab,
    isLoaded,
    openTab,
    showInActiveTab,
    openDocsHomeTab,
    openDashboardsHomeTab,
    openCampaignsHomeTab,
    closeTab,
    switchToTab,
    consumeSkipNextActiveTabEffect,
    updateTab,
    setActiveTabViewMode,
    canGoBack,
    canGoForward,
    goBack,
    goForward,
    reorderTabs,
    closeOtherTabs,
    closeAllTabs,
    findTab,
    getActiveTabPath,
  };

  return (
    <TabsContext.Provider value={value}>
      {children}
    </TabsContext.Provider>
  );
}

export function useTabs() {
  const context = useContext(TabsContext);
  if (!context) {
    throw new Error('useTabs must be used within a TabsProvider');
  }
  return context;
}

export default TabsContext;
